/**
 * The owner's tuned care numbers (docs/plans/016-care-curves-admin.md), fetched best effort from the public `GET /config/curves`.
 * The game never waits for them: the last good answer is kept on the device, and without any the built-in numbers apply.
 * Every answer is checked with the simulation's own validator before use, so a bad answer can never reach a pet.
 */
import { validateCareConfig, type CareConfig, type CurveProfile, type MeterId } from "@zookeepbit/sim";

const KEY = "zb-care-curves";
const SPECIES = "shark";
const TIMEOUT_MS = 3000;

export interface TunedCare {
  curves: Record<MeterId, Partial<CurveProfile>>;
  refillMs: Record<MeterId, number>;
}

export interface CareCurvesDeps {
  fetch: (input: string, init?: { signal?: AbortSignal }) => Promise<Response>;
  storage: Pick<Storage, "getItem" | "setItem"> | null;
  baseUrl?: string;
}

function toTuned(config: CareConfig): TunedCare {
  const refillMs = {} as Record<MeterId, number>;
  for (const id of Object.keys(config.refillSeconds) as MeterId[]) refillMs[id] = config.refillSeconds[id] * 1000;
  return { curves: config.curves, refillMs };
}

/** The config for the shark inside an answer, or null when it is missing or breaks the rules. */
function parse(raw: unknown): CareConfig | null {
  if (typeof raw !== "object" || raw === null) return null;
  const animals = (raw as { animals?: unknown }).animals;
  if (typeof animals !== "object" || animals === null) return null;
  const config = (animals as Record<string, unknown>)[SPECIES];
  if (config === undefined) return null;
  return validateCareConfig(SPECIES, config as CareConfig).ok ? (config as CareConfig) : null;
}

export function createCareCurves(deps: CareCurvesDeps) {
  let current: CareConfig | null = null;
  try {
    const stored = deps.storage?.getItem(KEY);
    if (stored) current = parse({ animals: { [SPECIES]: JSON.parse(stored) } });
  } catch {
    current = null;
  }

  return {
    /** The tuned numbers for a newly hatched shark, or null for the built-in ones. */
    get(): TunedCare | null {
      return current ? toTuned(current) : null;
    },
    /** Asks the server; on any failure the last good answer stays. An answer that says "nothing tuned" clears it. */
    async refresh(): Promise<void> {
      try {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
        let res: Response;
        try {
          res = await deps.fetch(`${deps.baseUrl ?? ""}/config/curves`, { signal: controller.signal });
        } finally {
          clearTimeout(timer);
        }
        if (!res.ok) return;
        const body: unknown = await res.json();
        const animals = typeof body === "object" && body !== null ? (body as { animals?: unknown }).animals : undefined;
        if (typeof animals !== "object" || animals === null) return;
        if (!(SPECIES in animals)) {
          current = null;
          try {
            deps.storage?.setItem(KEY, "");
          } catch {
            /* private mode */
          }
          return;
        }
        const config = parse(body);
        if (!config) return;
        current = config;
        try {
          deps.storage?.setItem(KEY, JSON.stringify(config));
        } catch {
          /* private mode: kept for this visit only */
        }
      } catch {
        /* offline or a bad answer: keep what we have */
      }
    },
  };
}
